import {
  Divider,
  GKButton,
  GKDatePicker,
  GKSearchSelect,
  GKSelect,
  GKTextField,
  Grid2,
  MenuItem,
  PhoneInput,
  ReloadIcon,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { AdvisorOnboardFormInterface } from "interfaces/OnboardingInterface";
import FamilyCreateModal from "Pages/Client/FamilyCreateModal";
import AddBranchModal from "Pages/Organisations/AddBranchModal";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  SELECTED_CITY,
  SELECTED_COUNTRY,
  SELECTED_STATE,
} from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { BRANCH_HAS_MANAGER } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { UserRoleTypes } from "utils/common";
import { MARITAL_STATUS, PERSONAL_GENDER } from "utils/constants";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import {
  emailRegex,
  panRegex,
  validateNotWhitespaceOnly,
} from "utils/validationSchema";
import AdvisorOnboardingClientBank from "./AdvisorOnboardingClientBank";
import AdvisorOnboardingClientNominee from "./AdvisorOnboardingClientNominee";

const AdvisorOnboardingClientDetails = ({
  hasError,
  setHasError,
}: {
  hasError?: boolean;
  setHasError?: any;
  setOpen?: (value: boolean) => void;
}) => {
  const form = useFormContext<AdvisorOnboardFormInterface>();

  const onboardingType: string[] = form.watch("onboardingType") || [];
  const isMfOnboarding = onboardingType.includes("MF");

  const userData = useAppSelector((state) => state?.user?.userData);
  const familyList = useAppSelector((state) => state?.app?.familyList);
  const partnerList = useAppSelector((state) => state?.app?.partnerList);
  const countryList = useAppSelector((state) => state?.app?.countryList);
  const stateList = useAppSelector((state) => state?.app?.stateList);
  const cityList = useAppSelector((state) => state?.app?.cityList);

  const dispatch = useAppDispatch();

  const [isBranchCreateModalOpen, setIsBranchCreateModalOpen] =
    useState<boolean>(false);

  const [birthCountryStates, setBirthCountryStates] = useState<any[]>([]);

  const [isFamilyCreateModalOpen, setIsFamilyCreateModalOpen] =
    useState<boolean>(false);

  // ===========================================================================================
  const { data: branchData } = useFetchQuery({
    key: ["GET_ALL_BRANCHES"],
    route: BRANCH_HAS_MANAGER,
  });

  // const { mutate: handleClientSelfOnboarding } = useMutation({
  //   mutationKey: ["HANDLE_CLIENT_SEND_LINK_MUTATUION"],
  //   mutationFn: (values: AdvisorOnboardFormInterface) =>
  //     axiosInstance.post(`/client/self-onboarding/`, {
  //       basketId: values?.basketObject?.id || null,
  //       feePlanId: values?.selectedFeePlan?.id || null,
  //       partnerId: values?.partner || userData?.partnerId || null,
  //       clientName: values?.personalDetails?.name,
  //       phone: values?.phone,
  //       clientEmail: values?.personalDetails.email,
  //       companyId: userData?.companyId,
  //       skipAdvanceFee: values?.skipAdvanceFee,
  //       clientId: values?.clientId,
  //     }),
  //   onSuccess: () => {
  //     setOpen(false);
  //   },
  // });

  // ===========================================================================================
  const { mutate: handleAddClient } = useMutation({
    mutationKey: ["HANDLE_ADD_CLIENT_MUTATUION"],
    mutationFn: (clientState: AdvisorOnboardFormInterface) =>
      axiosInstance.post("/client/", {
        basketId: clientState?.basketObject?.id || null,
        feePlanId: clientState?.selectedFeePlan?.id || null,
        branch: branchData?.data?.[0]?.id || null,
        phone: clientState.phone,
        countryCode: clientState.countryCode,
        family: clientState.family,
        onboardingType: clientState.onboardingType,
        mfEntity: clientState.mfEntity,
        bankAccounts: clientState.bankAccounts || [],
        nominees: clientState.nominees || [],
        partner: clientState.partner || userData?.partnerId,
        personalDetails: {
          email: clientState.personalDetails.email,
          name: clientState.personalDetails.name,
          gender: clientState.personalDetails.gender,
          state: clientState.personalDetails.state,
          country: clientState.personalDetails.country,
          maritalStatus: clientState.personalDetails.maritalStatus,
          fatherName: clientState.personalDetails.fatherName,
          motherName: clientState.personalDetails.motherName,
          dateOfBirth: clientState.personalDetails.dateOfBirth,
          address: clientState.personalDetails.address,
          income: clientState.personalDetails.income,
          occupation: clientState.personalDetails.occupation,
          pan: clientState.personalDetails.pan,
          city: clientState?.personalDetails?.city,
          pincode: clientState?.personalDetails?.pincode,
          aadhar: clientState?.personalDetails?.aadhar,
          emergencyContact: clientState?.personalDetails?.emergencyContact,
          emergencyContactNum:
            clientState?.personalDetails?.emergencyContactNum,
          country_of_birth: clientState?.personalDetails?.country_of_birth,
          place_of_birth: clientState?.personalDetails?.place_of_birth,
          nationality_country:
            clientState?.personalDetails?.nationality_country,
          source_of_wealth: clientState?.personalDetails?.source_of_wealth,
        },
      }),
    onSuccess: (response) => {
      form.reset({
        ...form.getValues(),
        ...response?.data,
        activeStep: form.getValues("activeStep") + 1,
      });
    },
  });

  // ===========================================================================================
  const { mutate: handleUpdateClient } = useMutation({
    mutationKey: ["HANDLE_UPDATE_CLIENT_MUTATUION"],
    mutationFn: (clientState: AdvisorOnboardFormInterface) =>
      axiosInstance.patch(`/client/${clientState.id}/`, {
        basketId: clientState?.basketObject?.id || null,
        feePlanId: clientState?.selectedFeePlan?.id || null,
        branch: branchData?.data?.[0]?.id || null,
        onboardingType: clientState.onboardingType,
        mfEntity: clientState.mfEntity,
        family: clientState.family,
        countryCode: clientState.countryCode,
        phone: clientState.phone,
        partner: clientState.partner || userData?.partnerId,
        bankAccounts: clientState.bankAccounts || [],
        nominees: clientState.nominees || [],
        personalDetails: {
          email: clientState.personalDetails.email,
          name: clientState.personalDetails.name,
          gender: clientState.personalDetails.gender,
          maritalStatus: clientState.personalDetails.maritalStatus,
          fatherName: clientState.personalDetails.fatherName,
          motherName: clientState.personalDetails.motherName,
          dateOfBirth: clientState.personalDetails.dateOfBirth,
          address: clientState.personalDetails.address,
          income: clientState.personalDetails.income,
          occupation: clientState.personalDetails.occupation,
          pan: clientState.personalDetails.pan,
          city: clientState?.personalDetails?.city,
          state: clientState?.personalDetails?.state,
          country: clientState?.personalDetails?.country,
          pincode: clientState?.personalDetails?.pincode,
          aadhar: clientState?.personalDetails?.aadhar,
          emergencyContact: clientState?.personalDetails?.emergencyContact,
          emergencyContactNum:
            clientState?.personalDetails?.emergencyContactNum,
          country_of_birth: clientState?.personalDetails?.country_of_birth,
          place_of_birth: clientState?.personalDetails?.place_of_birth,
          nationality_country:
            clientState?.personalDetails?.nationality_country,
          source_of_wealth: clientState?.personalDetails?.source_of_wealth,
        },
      }),
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_COMPLIANCE_BY_CLIENT_ID);
      refetchQuery(QueryKeys.GET_CLIENT_BY_ID);
      form.setValue("activeStep", form.getValues("activeStep") + 1);
    },
  });

  // ===========================================================================================
  const { mutate: handleGetKRAData, isLoading: KRALoading } = useMutation({
    mutationKey: ["GET_KRA_DATA"],
    mutationFn: (dataItem: any) => {
      return axiosInstance.post("/pan-details/", dataItem, {
        headers: { Authorization: null },
      });
    },
    onSuccess: (response) => {
      form.setValue("personalDetails.name", response.data.data.name);
      form.setValue("fetchedFromKra", true);
      form.setValue("personalDetails.address", response.data.data.address);
      form.setValue("personalDetails.gender", response.data.data.gender);
      form.setValue("personalDetails.pincode", response.data.data.pincode);
      form.setValue("personalDetails.city", response.data.data.city);
      form.setValue("personalDetails.country", response.data.data.country);
      form.setValue("personalDetails.state", response.data.data.state);
    },
  });

  return (
    <form
      id="advisor-onboarding-client-details-form"
      onSubmit={form.handleSubmit((values) => {
        if (form.getValues("id")) {
          handleUpdateClient(values);
          return;
        }
        handleAddClient(values);
      })}
    >
      <Grid2 container columnSpacing={2} rowSpacing={3}>
        <Grid2 size={12}>
          <Controller
            name="personalDetails.name"
            control={form.control}
            rules={{
              required: {
                value: form.getValues("activeStep") === 1,
                message: "Please enter name",
              },
              validate: validateNotWhitespaceOnly,
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Name"
                requiredField
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.pan"
            rules={{
              required: {
                value: form.getValues("activeStep") === 1,
                message: "Please enter PAN",
              },
              pattern: {
                value: panRegex,
                message: "PAN should have this format ABCDE1234A",
              },
              maxLength: {
                value: 10,
                message: "PAN should have 10 character",
              },
              minLength: {
                value: 10,
                message: "PAN should have 10 character",
              },
            }}
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="PAN"
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                requiredField
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2
          size={6}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"flex-end"}
          gap={1}
        >
          <Controller
            name="personalDetails.dateOfBirth"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please enter date of birth",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKDatePicker
                {...field}
                disableFuture
                inputLabel="Date Of Birth"
                requiredField={isMfOnboarding}
                value={dayjs(field.value)}
                onChange={(value) => {
                  field.onChange(dayjs(value).format("YYYY-MM-DD"));
                }}
                minDate={dayjs("01-01-1901")}
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
          {!isMfOnboarding && (
            <GKButton
              startIcon={<ReloadIcon />}
              style={{
                marginLeft: "auto",
              }}
              loading={KRALoading}
              onClick={() => {
                handleGetKRAData({
                  phone: form.getValues("phone"),
                  pan: form.getValues("personalDetails.pan"),
                  dob: dayjs(
                    form.getValues("personalDetails.dateOfBirth"),
                  ).format("YYYY-MM-DD"),
                  company: +userData?.companyId,
                });
              }}
            >
              Fetch My Details
            </GKButton>
          )}
        </Grid2>
        {isMfOnboarding && (
          <Grid2 size={6}>
            <Controller
              name="personalDetails.aadhar"
              control={form.control}
              rules={{
                required: {
                  value: isMfOnboarding,
                  message: "Please enter Aadhaar number",
                },
                minLength: {
                  value: 12,
                  message: "Aadhaar number should have 12 digits",
                },
                maxLength: {
                  value: 12,
                  message: "Aadhaar number should have 12 digits",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Aadhaar No."
                  requiredField
                  placeholder="12 Digit Aadhar Number"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        )}
        {isMfOnboarding && (
          <Grid2
            size={6}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            <GKButton
              startIcon={<ReloadIcon />}
              loading={KRALoading}
              onClick={() => {
                handleGetKRAData({
                  phone: form.getValues("phone"),
                  pan: form.getValues("personalDetails.pan"),
                  dob: dayjs(
                    form.getValues("personalDetails.dateOfBirth"),
                  ).format("YYYY-MM-DD"),
                  company: +userData?.companyId,
                });
              }}
            >
              Fetch My Details
            </GKButton>
          </Grid2>
        )}
        <Grid2 size={12}>
          <Divider />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="phone"
            rules={{
              required: {
                value: form.getValues("activeStep") === 1,
                message: "Please enter phone number",
              },
            }}
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <PhoneInput
                {...field}
                onSelectChange={(event: any) => {
                  form.setValue("countryCode", event);
                }}
                onTextChange={(event: any) => {
                  field.onChange(event);
                }}
                inputLabel="Phone Number"
                onTextValue={field.value}
                requiredField
                onSelectValue={form.getValues("countryCode")}
                error={!!error || hasError}
                helperText={error?.message}
                hasError={hasError}
                setHasError={setHasError}
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="personalDetails.email"
            control={form.control}
            rules={{
              required: {
                value: form.getValues("activeStep") === 1,
                message: "Please enter email",
              },
              pattern: {
                value: emailRegex,
                message: "Please enter a valid mail",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Email"
                requiredField
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
          <Grid2 size={6}>
            <Controller
              name="partner"
              control={form.control}
              render={({ field }) => (
                <GKSearchSelect
                  disableClearable={false}
                  inputLabel="Partner"
                  options={partnerList || []}
                  onChange={(e, val) => {
                    field.onChange(val.value);
                  }}
                  getOptionLabel={(option) => option.name || option}
                />
              )}
            />
          </Grid2>
        </Show.When>

        <Grid2 size={6}>
          <Controller
            name="family"
            control={form.control}
            render={({ field, formState: { errors } }) => (
              <GKSearchSelect
                {...field}
                disableClearable
                inputLabel="Family"
                options={familyList || []}
                onChange={(e, val) => {
                  field.onChange(val?.value);
                }}
                value={
                  familyList?.find((data: any) => +data?.id === +field.value)
                    ?.name
                }
                getOptionLabel={(option) => option.name || option}
                error={Boolean(errors?.family)}
                helperText={errors?.family?.message as string}
              />
            )}
          />
        </Grid2>
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
          <Typography
            color="var(--neutral-500)"
            fontSize={12}
            whiteSpace={"nowrap"}
            fontWeight={600}
          >
            ADDRESS DETAILS
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="personalDetails.address"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please enter address",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Address"
                autoComplete="off"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.country"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select a country",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                inputLabel="Country"
                error={!!error}
                helperText={error?.message}
                options={countryList || []}
                getOptionLabel={(e) => e.name || e}
                onChange={(e, val) => {
                  field.onChange(val.name);
                  form.setValue("personalDetails.city", "");
                  form.setValue("personalDetails.state", "");
                  dispatch(SELECTED_COUNTRY(val.id));
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.state"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select a state",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                inputLabel="State"
                requiredField
                error={!!error}
                helperText={error?.message}
                options={stateList || []}
                getOptionLabel={(e) => e.name || e}
                onChange={(e, val) => {
                  field.onChange(val.name);
                  form.setValue("personalDetails.city", "");
                  dispatch(SELECTED_STATE(val.id));
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.city"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please select a city",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                inputLabel="City"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
                options={cityList || []}
                getOptionLabel={(e) => e.name || e}
                onChange={(e, val) => {
                  field.onChange(val.name);
                  dispatch(SELECTED_CITY(val.id));
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.pincode"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please enter pincode",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Pincode"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
          <Typography
            color="var(--neutral-500)"
            fontSize={12}
            whiteSpace={"nowrap"}
            fontWeight={600}
          >
            BANK DETAILS
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Grid2>
        <Grid2 size={12}>
          <AdvisorOnboardingClientBank />
        </Grid2>
        {isMfOnboarding && (
          <>
            <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
              <Typography
                color="var(--neutral-500)"
                fontSize={12}
                whiteSpace={"nowrap"}
                fontWeight={600}
              >
                NOMINEE DETAILS
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Grid2>
            <Grid2 size={12}>
              <AdvisorOnboardingClientNominee />
            </Grid2>
          </>
        )}
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
          <Typography
            color="var(--neutral-500)"
            fontSize={12}
            whiteSpace={"nowrap"}
            fontWeight={600}
          >
            PERSONAL INFORMATION
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.gender"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please select gender",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                disableClearable={false}
                inputLabel="Gender"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
                options={PERSONAL_GENDER || []}
                onChange={(event, val) => {
                  field.onChange(val?.value);
                }}
                getOptionLabel={(option) => option.name || option}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.maritalStatus"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please select marital status",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSelect
                {...field}
                inputLabel="Marital Status"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              >
                <MenuItem style={{ display: "none" }} value="">
                  Select Marital Status
                </MenuItem>
                {MARITAL_STATUS?.map((data) => (
                  <MenuItem value={data.value} key={data.value}>
                    {data.name}
                  </MenuItem>
                ))}
              </GKSelect>
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.fatherName"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please enter father's name",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Father's Name"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.motherName"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please enter mother's name",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Mother's Name"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        {isMfOnboarding && (
          <Grid2 size={6}>
            <Controller
              name="personalDetails.country_of_birth"
              control={form.control}
              rules={{
                required: {
                  value: isMfOnboarding,
                  message: "Please select country of birth",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Country of Birth"
                  error={!!error}
                  helperText={error?.message}
                  options={countryList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    if (!val?.id) return;
                    field.onChange(val.name);
                    form.setValue("personalDetails.place_of_birth", "");
                    setBirthCountryStates([]);
                    axiosInstance
                      .get(`/state/?country_id=${val.id}`)
                      .then((res) =>
                        setBirthCountryStates(res.data?.data || []),
                      );
                  }}
                />
              )}
            />
          </Grid2>
        )}
        {isMfOnboarding && (
          <Grid2 size={6}>
            <Controller
              name="personalDetails.place_of_birth"
              control={form.control}
              rules={{
                required: {
                  value: isMfOnboarding,
                  message: "Please select place of birth",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  value={
                    birthCountryStates.find((s) => s.name === field.value) ||
                    null
                  }
                  requiredField
                  inputLabel="Place of Birth"
                  error={!!error}
                  helperText={error?.message}
                  options={birthCountryStates}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val?.name || "");
                  }}
                />
              )}
            />
          </Grid2>
        )}
        {isMfOnboarding && (
          <Grid2 size={6}>
            <Controller
              name="personalDetails.nationality_country"
              control={form.control}
              rules={{
                required: {
                  value: isMfOnboarding,
                  message: "Please select nationality",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Nationality"
                  error={!!error}
                  helperText={error?.message}
                  options={countryList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                  }}
                />
              )}
            />
          </Grid2>
        )}
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
          <Typography
            color="var(--neutral-500)"
            fontSize={12}
            whiteSpace={"nowrap"}
            fontWeight={600}
          >
            PROFESSIONAL & FINANCIAL
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.income"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please select income slab",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSelect
                {...field}
                value={typeof field.value === "string" ? field.value : ""}
                inputLabel="Income Slab"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              >
                <MenuItem style={{ display: "none" }} value="">
                  Select Income Range
                </MenuItem>
                {[
                  { label: "Upto 1 Lakh", value: "upto_1lakh" },
                  {
                    label: "Above 1 Lakh upto 5 Lakh",
                    value: "above_1lakh_upto_5lakh",
                  },
                  {
                    label: "Above 5 Lakh upto 10 Lakh",
                    value: "above_5lakh_upto_10lakh",
                  },
                  {
                    label: "Above 10 Lakh upto 25 Lakh",
                    value: "above_10lakh_upto_25lakh",
                  },
                  {
                    label: "Above 25 Lakh upto 1 Cr",
                    value: "above_25lakh_upto_1cr",
                  },
                  { label: "Above 1 Cr", value: "above_1cr" },
                ].map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </GKSelect>
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="personalDetails.occupation"
            control={form.control}
            rules={{
              required: {
                value: isMfOnboarding,
                message: "Please select occupation type",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSelect
                {...field}
                inputLabel="Occupation Type"
                requiredField={isMfOnboarding}
                error={!!error}
                helperText={error?.message}
              >
                <MenuItem style={{ display: "none" }} value="">
                  Select Occupation Type
                </MenuItem>
                {[
                  { label: "Business", value: "business" },
                  { label: "Professional", value: "professional" },
                  { label: "Retired", value: "retired" },
                  { label: "House Wife", value: "house_wife" },
                  { label: "Student", value: "student" },
                  {
                    label: "Public Sector Service",
                    value: "public_sector_service",
                  },
                  {
                    label: "Private Sector Service",
                    value: "private_sector_service",
                  },
                  {
                    label: "Government Service",
                    value: "government_service",
                  },
                  { label: "Others", value: "others" },
                  { label: "Agriculture", value: "agriculture" },
                  { label: "Doctor", value: "doctor" },
                  { label: "Forex Dealer", value: "forex_dealer" },
                  { label: "Service", value: "service" },
                ].map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </GKSelect>
            )}
          />
        </Grid2>
        {isMfOnboarding && (
          <Grid2 size={6}>
            <Controller
              name="personalDetails.source_of_wealth"
              control={form.control}
              rules={{
                required: {
                  value: isMfOnboarding,
                  message: "Please select source of wealth",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSelect
                  {...field}
                  inputLabel="Source of Wealth"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem style={{ display: "none" }} value="">
                    Select Income Range
                  </MenuItem>
                  {[
                    { label: "Salary", value: "salary" },
                    { label: "Business", value: "business" },
                    { label: "Gift", value: "gift" },
                    {
                      label: "Ancestral Property",
                      value: "ancestral_property",
                    },
                    { label: "Rental Income", value: "rental_income" },
                    { label: "Prize Money", value: "prize_money" },
                    { label: "Royalty", value: "royalty" },
                    { label: "Others", value: "others" },
                  ].map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </GKSelect>
              )}
            />
          </Grid2>
        )}
      </Grid2>
      {isBranchCreateModalOpen && (
        <AddBranchModal
          open={isBranchCreateModalOpen}
          updateData={null}
          setOpen={setIsBranchCreateModalOpen}
          isEditModal={false}
        />
      )}
      {isFamilyCreateModalOpen && (
        <FamilyCreateModal
          open={isFamilyCreateModalOpen}
          setOpen={setIsFamilyCreateModalOpen}
        />
      )}
    </form>
  );
};

export default AdvisorOnboardingClientDetails;
